if (!customElements.get('azantic-product-media')) {
  class AzanticProductMedia extends HTMLElement {
    constructor() {
      super();
      this.carousel = null;
      this.carouselTrack = null;
      this.carouselItems = [];
      this.currentIndex = 0;
      this.leftArrow = null;
      this.rightArrow = null;
      
      // Touch/swipe variables
      this.startX = 0;
      this.currentX = 0;
      this.initialX = 0; // To track if it was a click or drag
      this.isDragging = false;
      this.hasMoved = false; // Flag to track if actual movement occurred
      this.dragThreshold = 50; // Minimum drag distance to trigger slide change
      
      // Bind methods to ensure proper 'this' context
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.carousel = this.querySelector('.carousel');
      this.carouselTrack = this.querySelector('.carousel-track');
      this.carouselItems = this.querySelectorAll('.carousel-item');
      this.leftArrow = this.querySelector('.product-card-gallery__arrow--left');
      this.rightArrow = this.querySelector('.product-card-gallery__arrow--right');

      if (!this.carousel || !this.carouselTrack || this.carouselItems.length <= 1) {
        return;
      }

      this.setupArrows();
      this.setupSwipe();
      this.setupHoverBehavior();
      this.setupClickHandling();
    }

    setupArrows() {
      if (this.leftArrow) {
        this.leftArrow.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.prevSlide();
        });
      }

      if (this.rightArrow) {
        this.rightArrow.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.nextSlide();
        });
      }
    }
    
    setupSwipe() {
      // Add touch event listeners for mobile swipe
      this.carousel.addEventListener('touchstart', (e) => this.handleDragStart(e), { passive: true });
      this.carousel.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: true });
      this.carousel.addEventListener('touchend', (e) => this.handleDragEnd(e));
      
      // Add mouse event listeners for desktop swipe
      this.carousel.addEventListener('mousedown', (e) => this.handleDragStart(e));
      this.carousel.addEventListener('mousemove', (e) => this.handleDragMove(e));
      this.carousel.addEventListener('mouseup', (e) => this.handleDragEnd(e));
      this.carousel.addEventListener('mouseleave', (e) => this.handleDragEnd(e));
    }
    
    setupHoverBehavior() {
      const productCard = this.closest('.azantic-product-card-gallery');
      if (!productCard) return;
      
      // Check if hover effect is disabled via the data attribute
      const isHoverDisabled = productCard.hasAttribute('data-disable-hover');
      
      // Pre-load variant buttons to avoid delay
      const preloadVariantButtons = () => {
        // Force the variant buttons to be rendered immediately
        const variantContainer = productCard.querySelector('.variant-buttons-container');
        if (variantContainer) {
          // Ensure the container is ready for immediate display
          variantContainer.style.transition = 'none';
          variantContainer.style.willChange = 'opacity';
        }
      };
      
      // Preload on page load
      preloadVariantButtons();
      
      // Always set up the data-state attribute changes for styling purposes
      productCard.addEventListener('mouseenter', () => {
        // Set state immediately without transition
        productCard.setAttribute('data-state', 'open');
        
        // Only change the slide if hover effect is not disabled
        if (!isHoverDisabled && this.carouselItems.length > 1) {
          this.goToSlide(1); // Go to second image
        }
      });

      productCard.addEventListener('mouseleave', () => {
        productCard.setAttribute('data-state', 'close');
        
        // Only return to first slide if hover effect is not disabled
        if (!isHoverDisabled) {
          this.goToSlide(0); // Return to first image on mouse leave
        }
      });
    }
    
    handleDragStart(e) {
      // Determine if this is a primary button click (left mouse button)
      // For touch events, we always proceed
      if (e.type.includes('mouse') && e.button !== 0) {
        return;
      }
      
      // Mark the start of a potential drag
      this.isDragging = true;
      this.startX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      this.carouselTrack.style.transition = 'none';
      
      // Store the initial position to determine if it was a click or drag
      this.initialX = this.startX;
      this.hasMoved = false; // Reset the movement flag
    }
    
    handleDragMove(e) {
      if (!this.isDragging) return;
      
      // Prevent default to avoid text selection during drag
      if (e.type.includes('mouse') && e.preventDefault) {
        e.preventDefault();
      }
      
      this.currentX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      const diffX = this.currentX - this.startX;
      
      // Only consider it a drag if moved more than a small threshold
      if (Math.abs(diffX) > 5) {
        this.hasMoved = true;
        
        const slideWidth = this.carousel.offsetWidth;
        
        // Calculate new position based on drag
        const newPosition = -this.currentIndex * slideWidth + diffX;
        this.carouselTrack.style.transform = `translateX(${newPosition}px)`;
      }
    }
    
    handleDragEnd(e) {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.carouselTrack.style.transition = 'transform 0.3s ease';
      
      // If we haven't actually moved, treat it as a click, not a drag
      if (!this.hasMoved) {
        return; // Allow the click to proceed normally
      }
      
      const diffX = this.currentX - this.startX;
      
      // If dragged far enough, change slide and prevent navigation
      if (Math.abs(diffX) > this.dragThreshold) {
        // Prevent the default click behavior that would navigate to the product page
        if (e && e.preventDefault) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        if (diffX > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
      } else {
        // If not dragged far enough, snap back to current slide
        this.goToSlide(this.currentIndex);
        
        // Even for small drags, prevent navigation if we've moved
        if (e && e.preventDefault) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.carouselItems.length) return;
      
      this.currentIndex = index;
      const slideWidth = this.carousel.offsetWidth;
      this.carouselTrack.style.transform = `translateX(-${slideWidth * index}px)`;
    }
    
    setupClickHandling() {
      // Get the product card link element
      const productCard = this.closest('.azantic-product-card-gallery');
      if (!productCard) return;
      
      // Add a click handler to the carousel to prevent navigation when dragging
      this.carousel.addEventListener('click', this.handleClick);
    }
    
    handleClick(e) {
      // If we've moved during this interaction, prevent the click
      if (this.hasMoved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    nextSlide() {
      const nextIndex = this.currentIndex + 1;
      if (nextIndex >= this.carouselItems.length) {
        this.goToSlide(0); // Loop back to first slide
      } else {
        this.goToSlide(nextIndex);
      }
    }

    prevSlide() {
      const prevIndex = this.currentIndex - 1;
      if (prevIndex < 0) {
        this.goToSlide(this.carouselItems.length - 1); // Loop to last slide
      } else {
        this.goToSlide(prevIndex);
      }
    }
  }

  customElements.define('azantic-product-media', AzanticProductMedia);
}
